import React from 'react';
import { UsersIcon, MapPinIcon, FileTextIcon, ServerIcon, SmartphoneIcon, MessageSquareIcon, MailIcon, PhoneIcon, MonitorIcon, Share2Icon, Building2Icon, DatabaseIcon, CloudIcon, LandmarkIcon, PlugIcon, WorkflowIcon, PlayIcon, BarChart3Icon, ArrowUpIcon, AlertTriangleIcon, Clock3Icon, ArrowRightIcon, CameraIcon } from 'lucide-react';
import { FAMILY_PHOTO, WATER_MAIN_PHOTO, solutionInputs, solutionChannels, liveResults, type Bi } from '../../../data/communicationsSolution';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
function MapThumb() {
  return <svg viewBox="0 0 96 72" className="w-full h-full" aria-hidden="true" data-id="element-2880" data-mp="vdfgk14fn709">
      <rect width="96" height="72" fill="#EEF2F0" data-id="element-2881" data-mp="qg5rimkygxcy" />
      <g stroke="#FFFFFF" strokeWidth="2" data-id="element-2882" data-mp="1nwl1en5avie0">
        <line x1="0" y1="18" x2="96" y2="10" data-id="element-2883" data-mp="1nxnqqqmz5crt" />
        <line x1="0" y1="44" x2="96" y2="36" data-id="element-2884" data-mp="1j5mhzep0m46j" />
        <line x1="22" y1="0" x2="30" y2="72" data-id="element-2885" data-mp="1j3rxu2lkig3i" />
        <line x1="62" y1="0" x2="70" y2="72" data-id="element-2886" data-mp="2shmgpmgxt2t7" />
      </g>
      <path d="M26 14 L60 8 L76 28 L66 58 L34 62 L18 38 Z" fill="#7C6CF4" fillOpacity="0.55" stroke="#5B4BE0" strokeWidth="1" data-id="element-2887" data-mp="2shw7vddpxg0h" />
      {[[36, 24], [48, 30], [58, 22], [42, 44], [56, 46], [30, 36], [64, 38], [50, 54]].map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill="#2E1FA8" data-id="element-2888" data-mp="2nqnwwcwqpml7" />)}
    </svg>;
}
const DATA_TILES = [{
  Icon: Building2Icon,
  bg: 'bg-[#EEEDFD]',
  fg: 'text-[#4F46E5]'
}, {
  Icon: DatabaseIcon,
  bg: 'bg-[#1E1B4B]',
  fg: 'text-white'
}, {
  Icon: LandmarkIcon,
  bg: 'bg-[#F4F3FD]',
  fg: 'text-[#4F46E5]'
}, {
  Icon: CloudIcon,
  bg: 'bg-[#FFF1E2]',
  fg: 'text-[#E07B12]'
}, {
  Icon: PlugIcon,
  bg: 'bg-[#4F46E5]',
  fg: 'text-white'
}, {
  Icon: WorkflowIcon,
  bg: 'bg-[#EAF3FE]',
  fg: 'text-[#2F74D0]'
}];
const INPUT_ICONS = {
  identity: UsersIcon,
  context: MapPinIcon,
  event: FileTextIcon,
  data: ServerIcon
} as const;
export function SolutionInputCard({
  isFR,
  item
}: P & {
  item: (typeof solutionInputs)[number];
}) {
  const pick = pk(isFR);
  const Icon = INPUT_ICONS[item.key];
  return <div className="h-full rounded-2xl border border-[#EEEEF4] bg-white px-4 py-3 flex items-center gap-3.5 shadow-[0_10px_26px_-22px_rgba(40,36,120,0.45)]" data-id="element-2889" data-mp="3spkkkvvzu8l1">
      <span className="w-11 h-11 rounded-full bg-[#F4F3FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2890" data-mp="3nqzrrhj2mr6m">
        <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-2891" data-mp="3ithfbh5uhttv" />
      </span>
      <span className="flex-1 min-w-0" data-id="element-2892" data-mp="k8y3xyx7l5ut">
        <span className="block font-sans text-[14.5px] font-medium text-[#121212]" data-id="element-2893" data-mp="fawlng7lxake">{pick(item.title)}</span>
        <span className="block font-sans text-[12px] text-[#5B6478] leading-snug mt-1" data-id="element-2894" data-mp="ajej4npxr1xb">{pick(item.body)}</span>
      </span>
      <span className="w-[88px] h-[72px] shrink-0 rounded-lg overflow-hidden" aria-hidden="true" data-id="element-2895" data-mp="5jtmwl25h7oa">
        {item.key === 'identity' && <img src={FAMILY_PHOTO} alt="" className="w-full h-full object-cover" data-id="element-2896" data-mp="131jwoactkgb6" />}
        {item.key === 'context' && <MapThumb data-id="element-2897" data-mp="y7fmli89bs5v" />}
        {item.key === 'event' && <img src={WATER_MAIN_PHOTO} alt="" className="w-full h-full object-cover" data-id="element-2898" data-mp="15lgj7iugf7xn" />}
        {item.key === 'data' && <span className="grid grid-cols-3 gap-1.5 place-content-center h-full" data-id="element-2899" data-mp="1akjck612npps">
            {DATA_TILES.map(({
          Icon: T,
          bg,
          fg
        }, i) => <span key={i} className={`w-6 h-6 rounded-md flex items-center justify-center ${bg}`} data-id="element-2900" data-mp="1fl7e3lmxfoy9" data-mpi={i}>
                <T className={`w-3.5 h-3.5 ${fg}`} strokeWidth={1.8} data-id="element-2901" data-mp="1kfl5lcygxbxn" />
              </span>)}
          </span>}
      </span>
    </div>;
}
function Lines({
  n,
  w = 'w-full'
}: {
  n: number;
  w?: string;
}) {
  return <span className={`flex flex-col gap-[3px] ${w}`} data-id="element-2902" data-mp="1y92n6s7lgel1">
      {Array.from({
      length: n
    }).map((_, i) => <span key={i} className="h-[3px] rounded-full bg-[#DCDDE6]" style={{
      width: `${90 - i * 18}%`
    }} data-id="element-2903" data-mp="237dv2xebwf0m" data-mpi={i} />)}
    </span>;
}
function ChannelPreview({
  k
}: {
  k: (typeof solutionChannels)[number]['key'];
}) {
  if (k === 'push' || k === 'web') return <span className={`w-[64px] h-[44px] rounded-lg p-1.5 flex flex-col gap-1 ${k === 'push' ? 'bg-[#2A2466]' : 'bg-[#EEF0F7]'}`} data-id="element-2904" data-mp="39y04xmk8weip">
        <span className="rounded bg-white/95 p-1 flex items-center gap-1" data-id="element-2905" data-mp="34xu9dvauu7vc">
          <span className="w-2 h-2 rounded-sm bg-[#4F46E5] shrink-0" data-id="element-2906" data-mp="3iq231e6udipt" />
          <Lines n={2} data-id="element-2907" data-mp="3iseldsgihv3e" />
        </span>
        <span className="rounded bg-white/80 p-1" data-id="element-2908" data-mp="3eo6uppqhu52j">
          <Lines n={2} data-id="element-2909" data-mp="3eqt9oafprmus" />
        </span>
      </span>;
  if (k === 'sms') return <span className="w-[64px] h-[36px] rounded-lg bg-[#EEF0F7] p-1.5 flex items-center" data-id="element-2910" data-mp="2qze4m9wjyrfu">
        <Lines n={3} data-id="element-2911" data-mp="2m1m2kn271oxx" />
      </span>;
  if (k === 'email') return <span className="w-[64px] h-[44px] rounded-lg border border-[#E6E7EE] bg-white p-1.5 flex flex-col gap-1" data-id="element-2912" data-mp="300vgfg2xg6yq">
        <span className="h-[4px] w-8 rounded-full bg-[#4F46E5]/70" data-id="element-2913" data-mp="2uzy2jfoy9z31" />
        <Lines n={3} data-id="element-2914" data-mp="f91z8pjz3u7v" />
      </span>;
  if (k === 'voice') return <span className="flex items-center gap-1.5" data-id="element-2915" data-mp="aaieutl12qhm">
        <span className="w-5 h-5 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-2916" data-mp="a9p4limv1iuq">
          <PlayIcon className="w-2.5 h-2.5 text-white fill-white" data-id="element-2917" data-mp="iqpqahx6emfc" />
        </span>
        <span className="flex items-center gap-[2px]" data-id="element-2918" data-mp="mtclmst22pha">
          {[4, 8, 12, 6, 10, 14, 7, 11, 5, 9, 6].map((h, i) => <span key={i} className="w-[2px] rounded-full bg-[#8B7FF7]" style={{
        height: h
      }} data-id="element-2919" data-mp="8ummz8ql8hqt" data-mpi={i} />)}
        </span>
      </span>;
  return <span className="flex items-center gap-1" data-id="element-2920" data-mp="11i0hz1aq738a">
      <span className="w-5 h-5 rounded-md bg-[#1877F2] flex items-center justify-center font-sans text-[11px] font-bold text-white" data-id="element-2921" data-mp="11jbh2nt87mt2">f</span>
      <span className="w-5 h-5 rounded-md bg-[#D62976] flex items-center justify-center" data-id="element-2922" data-mp="16iwkpbjccjc4">
        <CameraIcon className="w-3 h-3 text-white" strokeWidth={2} data-id="element-2923" data-mp="rqctn99gu2b1" />
      </span>
      <span className="w-5 h-5 rounded-md bg-[#111111] flex items-center justify-center font-sans text-[10px] font-bold text-white" data-id="element-2924" data-mp="wpfok0vqes5a">X</span>
    </span>;
}
const CH_ICONS = {
  push: SmartphoneIcon,
  sms: MessageSquareIcon,
  email: MailIcon,
  voice: PhoneIcon,
  web: MonitorIcon,
  social: Share2Icon
} as const;
export function SolutionChannelCard({
  isFR,
  item
}: P & {
  item: (typeof solutionChannels)[number];
}) {
  const Icon = CH_ICONS[item.key];
  return <div className="h-full rounded-xl border border-[#EEEEF4] bg-white px-4 py-2 flex items-center gap-3 shadow-[0_8px_20px_-18px_rgba(40,36,120,0.45)]" data-id="element-2925" data-mp="1p4uhutbr7ejc">
      <Icon className="w-5 h-5 text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-2926" data-mp="1l5v7uub60rl1" />
      <span className="flex-1 font-sans text-[13px] text-[#121212] whitespace-nowrap" data-id="element-2927" data-mp="1g60jaxev4e35">{pk(isFR)(item.label)}</span>
      <span aria-hidden="true" data-id="element-2928" data-mp="1bc5x9nbsbn0w">
        <ChannelPreview k={item.key} data-id="element-2929" data-mp="16ecr2uahet0m" />
      </span>
    </div>;
}
const KPI_ICONS = {
  missed: AlertTriangleIcon,
  retry: Clock3Icon,
  calls: UsersIcon
} as const;
export function LiveResultsPanel({
  isFR
}: P) {
  const pick = pk(isFR);
  const r = liveResults;
  const C = 2 * Math.PI * 40;
  return <div className="h-full rounded-2xl border border-[#EEEEF4] bg-white p-5 flex flex-col gap-5 shadow-[0_18px_40px_-30px_rgba(40,36,120,0.45)]" data-id="element-2930" data-mp="selcarc558lk">
      <p className="flex items-center gap-3 font-sans text-[15px] font-medium text-[#121212]" data-id="element-2931" data-mp="3426zdn1f5nwd">
        <BarChart3Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-2932" data-mp="38zqbagan9ol5" />
        {pick(r.title)}
      </p>
      <div className="flex items-center gap-4" data-id="element-2933" data-mp="3i299uo7edf1l">
        <div className="relative w-[96px] h-[96px] shrink-0" data-id="element-2934" data-mp="3i0y9kyd5upf8">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90" aria-hidden="true" data-id="element-2935" data-mp="2lc8n0uql98mx">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#DDF3E5" strokeWidth="11" data-id="element-2936" data-mp="2pdaq5mnmm4u6" />
            <circle cx="50" cy="50" r="40" fill="none" stroke="#22A04F" strokeWidth="11" strokeLinecap="round" strokeDasharray={`${C * r.ratePct / 100} ${C}`} data-id="element-2937" data-mp="2ucne6aq8hhyc" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center font-sans text-[18px] font-semibold text-[#121212]" data-id="element-2938" data-mp="26n2prcbb9sho">{pick(r.rate)}</span>
        </div>
        <div className="min-w-0" data-id="element-2939" data-mp="1000gtb2wln3h">
          <p className="font-sans text-[24px] font-semibold text-[#121212] leading-none tabular-nums" data-id="element-2940" data-mp="v0nl00u215or">{pick(r.reached)}</p>
          <p className="font-sans text-[13px] text-[#5B6478] mt-1" data-id="element-2941" data-mp="190ou23kh3jfb">{pick(r.reachedLabel)}</p>
          <span className="mt-2 inline-flex items-center gap-1 rounded px-2 py-0.5 bg-[#EAF7EE] font-sans text-[11px] text-[#1E8A45] whitespace-nowrap" data-id="element-2942" data-mp="14ybp3hdjvpjl">
            <ArrowUpIcon className="w-3 h-3" aria-hidden="true" data-id="element-2943" data-mp="1iwji5e7gu6ph" />
            {pick(r.trend)}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2" data-id="element-2944" data-mp="1so60rvm4v829">
        {r.kpis.map(k => {
        const Icon = KPI_ICONS[k.key];
        const warn = k.key === 'missed';
        const big = k.key !== 'retry';
        return <div key={k.key} className={`rounded-xl px-2 py-3 flex flex-col items-center text-center ${warn ? 'bg-[#FFF5F4]' : 'bg-[#F7F7FB]'}`} data-id="element-2945" data-mp="21q5jcscsqk7b">
              <Icon className={`w-5 h-5 ${warn ? 'text-[#E23E3E]' : 'text-[#4F46E5]'}`} strokeWidth={1.7} aria-hidden="true" data-id="element-2946" data-mp="1wny3vssrcm08" />
              <p className={`font-sans text-[#121212] mt-2 leading-tight ${big ? 'text-[18px] font-semibold' : 'text-[12px]'}`} data-id="element-2947" data-mp="2lankrb1bj586">{pick(k.value)}</p>
              <p className="font-sans text-[11.5px] text-[#5B6478] leading-tight" data-id="element-2948" data-mp="27al5lwkeapwm">{pick(k.label)}</p>
            </div>;
      })}
      </div>
      <div className="mt-auto rounded-xl bg-[#F4F3FD] px-4 py-3.5 flex items-center gap-3" data-id="element-2949" data-mp="1xll528q8vmgb">
        <span className="w-8 h-8 rounded-md bg-[#4F46E5] flex items-center justify-center font-sans text-[11px] font-bold text-white shrink-0" aria-hidden="true" data-id="element-2950" data-mp="22c1ms513lu8h">
          {isFR ? 'IA' : 'AI'}
        </span>
        <div className="flex-1 min-w-0" data-id="element-2951" data-mp="1slyq9uurpy5e">
          <p className="font-sans text-[13px] font-medium text-[#4F46E5]" data-id="element-2952" data-mp="1smrr6f71ptfm">{pick(r.atna.title)}</p>
          <p className="font-sans text-[13px] text-[#121212] leading-snug mt-1" data-id="element-2953" data-mp="1emz4qw2jxzdg">{pick(r.atna.body)}</p>
        </div>
        <button type="button" aria-label={pick(r.atna.action)} className="w-10 h-10 rounded-full bg-white shadow-[0_6px_14px_-8px_rgba(79,70,229,0.6)] flex items-center justify-center shrink-0 transition-colors duration-150 hover:bg-[#EEEDFD]" data-id="element-2954" data-mp="1jkqtjh7wh5df">
          <ArrowRightIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-2955" data-mp="r80oecgupb4j" />
        </button>
      </div>
    </div>;
}